<template>
  <titan-accordion position="right" animate class="mobile-desktop-app" @toggle="onAccordionToggle">
    <span class="mobile-desktop-app__title">
      {{
        $t(
          isNeo
            ? "emailAccounts.successScreen.appDownload.headingMobileOnly"
            : "emailAccounts.successScreen.appDownload.heading"
        )
      }}
      <!--
        Inline platform-availability glyphs next to the heading, so the
        collapsed accordion telegraphs which app stores it covers
        without forcing the user to expand it. The Windows glyph is desktop-
        only, so it's dropped for Neo (no electron app — see `isNeo`).
      -->
      <span class="mobile-desktop-app__platform-icons" aria-hidden="true">
        <img src="@/assets/play-store-icon.svg" alt="" class="mobile-desktop-app__platform-icon" />
        <img
          v-if="!isNeo"
          src="@/assets/windows.svg"
          alt=""
          class="mobile-desktop-app__platform-icon"
        />
        <img src="@/assets/apple.svg" alt="" class="mobile-desktop-app__platform-icon" />
      </span>
    </span>

    <template #content>
      <div class="mobile-desktop-app__content">
        <div class="mobile-desktop-app__row">
          <div v-if="brandDetails.mobileAppQr" class="mobile-desktop-app__qr">
            <img :src="brandDetails.mobileAppQr" alt="qr-code" width="115" height="115" />
          </div>

          <div v-if="brandDetails.mobileAppQr" class="mobile-desktop-app__divider">
            {{ $t("mobileScreen.or").toUpperCase() }}
          </div>

          <div class="mobile-desktop-app__store-buttons">
            <android-download-button source-hook="email account creation success" />
            <apple-download-button source-hook="email account creation success" />
          </div>
        </div>

        <!--
          OS-specific desktop download, mirroring the DeviceDownload page:
          gated on the brand's macDesktop/winDesktop URLs (absent for Neo, so
          nothing shows there) and the detected platform. Each button leads
          with its OS glyph. Direct download — no Windows security-prompt modal.
        -->
        <div class="mobile-desktop-app__desktop-buttons">
          <a
            v-if="brandDetails.download.macDesktop && showMacButton"
            :href="brandDetails.download.macDesktop"
            target="_blank"
            rel="noopener"
            class="button button__line-blue mobile-desktop-app__desktop-btn"
            @click="onDesktopDownloadClick('mac')"
          >
            <i
              class="mobile-desktop-app__os-icon mobile-desktop-app__os-icon--mac"
              aria-hidden="true"
            />
            {{ $t("menuItemsDescription.desktopAppForMac") }}
          </a>
          <a
            v-if="brandDetails.download.winDesktop && showWindowsButton"
            :href="brandDetails.download.winDesktop"
            target="_blank"
            rel="noopener"
            class="button button__line-blue mobile-desktop-app__desktop-btn"
            @click="onDesktopDownloadClick('windows')"
          >
            <i
              class="mobile-desktop-app__os-icon mobile-desktop-app__os-icon--windows"
              aria-hidden="true"
            />
            {{ $t("menuItemsDescription.desktopAppForWindows") }}
          </a>
        </div>
      </div>
    </template>
  </titan-accordion>
</template>

<script lang="ts">
import Vue from "vue";
import AndroidDownloadButton from "../Utility/AndroidDownloadButton.vue";
import AppleDownloadButton from "../Utility/AppleDownloadButton.vue";
import TitanAccordion from "../core/TitanAccordion.vue";
import brand from "@/helpers/brand";
import { isNeoBrand } from "@/helpers";
import { getOSPlatform } from "@/helpers/device";
import {
  trackDownloadAppAccordionExpanded,
  trackDownloadNativeAppsButtonClick,
} from "@/telemetry/mailboxNudgeEvents";
import type { TCreateEmailSourceHook } from "@/telemetry/mailboxNudgeEvents";

/**
 * Collapsible "Get mobile and desktop app" panel on the Create-email
 * success screen. Wraps the generic TitanAccordion (chevron on right)
 * around content reused from the existing DeviceDownload page:
 * brand-aware QR (`brandDetails.mobileAppQr`) on the left, an OR divider,
 * and the Play/App Store badge buttons (which already own their own
 * medusa click events). Below the row, OS-specific "Download desktop app"
 * buttons mirror the DeviceDownload page — gated on the brand's
 * macDesktop/winDesktop URLs and the detected platform.
 *
 * Open/closed state is owned by TitanAccordion's internal TitanToggle —
 * nothing to manage here.
 */
export default Vue.extend({
  name: "MobileDesktopAppAccordion",
  components: { AndroidDownloadButton, AppleDownloadButton, TitanAccordion },
  props: {
    /** Analytics: whether the just-created mailbox was the order's first. */
    isFirstMailbox: {
      type: Boolean,
      default: false,
    },
    /** Analytics: whether the just-created mailbox is an admin mailbox. */
    isAdminMailbox: {
      type: Boolean,
      default: false,
    },
    /**
     * Analytics: the source that launched the create flow — the expanded
     * event reports the same source_hook as new_email_creation_viewed.
     */
    creationSourceHook: {
      type: String,
      default: "create_email_account_tab",
    },
  },
  data() {
    // Detected platform gates which desktop button(s) show: a known OS
    // surfaces only its own download; an unknown OS surfaces both. Same
    // approach as the DeviceDownload page.
    const osPlatform = getOSPlatform();
    return {
      brandDetails: brand.getDetails(),
      // Neo has no electron/desktop app, so the Windows heading glyph and
      // the "desktop" heading wording are dropped (the desktop buttons
      // themselves drop out via the absent macDesktop/winDesktop URLs).
      isNeo: isNeoBrand(),
      showMacButton: !osPlatform || osPlatform === "mac",
      showWindowsButton: !osPlatform || osPlatform === "windows",
    };
  },
  methods: {
    onAccordionToggle(open: boolean): void {
      if (!open) return;
      trackDownloadAppAccordionExpanded({
        sourceHook: this.creationSourceHook as TCreateEmailSourceHook,
        isFirstMailbox: this.isFirstMailbox,
        isAdmin: this.isAdminMailbox,
      });
    },
    // Analytics only — the anchor's href performs the actual download.
    onDesktopDownloadClick(platform: "mac" | "windows"): void {
      trackDownloadNativeAppsButtonClick({
        buttonName: platform === "mac" ? "mac_desktop_app" : "windows_desktop_app",
        sourceHook: "email account creation success",
      });
    },
  },
});
</script>

<style lang="scss" scoped>
.mobile-desktop-app {
  // Root-level overrides win by scoped-class specificity (0,2,0) over
  // TitanAccordion's `:where(...)`-wrapped defaults (0,1,0).
  padding: 12px 16px;
  border-radius: 4px;
  margin-top: 24px;
  max-width: 672px;
  background: #f3f3f3;

  // Replace the library's chevron-asset (which rotates 90° when open)
  // with a plain ▼/▲ caret for this accordion only — done from the host
  // via ::v-deep so TitanAccordion's other consumers (AddonStatusTable)
  // keep their existing chevron-asset behavior. CSS-drawn so no new asset.
  ::v-deep .chevron {
    background: none;
    border: solid var(--secondaryTextCol);
    border-width: 0 2px 2px 0;
    width: 10px;
    height: 10px;
    transform: rotate(45deg); // ▼ closed
    transition: transform 0.15s ease-in-out;

    &.open {
      transform: rotate(-135deg); // ▲ open
    }
  }

  ::v-deep header {
    padding: 0;
  }

  &__title {
    flex: 1;
    color: var(--primaryTextCol);
    font-size: 15px;
    font-weight: 600;
    display: inline-flex;
    align-items: center;
    gap: 10px;
  }

  &__platform-icons {
    display: inline-flex;
    align-items: center;
    gap: 4px;
  }

  &__platform-icon {
    width: 16px;
    height: 16px;
    display: block;
  }

  &__content {
    padding: 16px 0 4px;
    width: max-content;
  }

  &__row {
    display: flex;
    align-items: center;
    gap: 24px;
    padding-bottom: 16px;
  }

  &__qr {
    flex-shrink: 0;
    img {
      display: block;
      width: 115px;
      height: 115px;
    }
  }

  &__divider {
    color: var(--secondaryTextCol);
    font-size: 12px;
    font-weight: 600;
    letter-spacing: 0.5px;
  }

  &__store-buttons {
    display: flex;
    flex-direction: column;
    gap: 12px;
    a {
      display: inline-block;
    }
    img {
      display: block;
      height: auto;
      max-width: 170px;
    }
  }

  &__desktop-buttons {
    display: flex;
    flex-direction: column;
    gap: 8px;
  }

  // Neutral (black text + grey border) rather than the blue `button__line-blue`
  // default — per design UAT these desktop-download buttons should read as
  // neutral, not a primary/link action. `.button` in the selector keeps this
  // ahead of the global `.button.button__line-blue` rule on specificity.
  &__desktop-btn.button {
    width: 100%;
    display: inline-flex;
    align-items: center;
    justify-content: center;
    gap: 8px;
    text-decoration: none;
    color: var(--primaryTextCol);
    border-color: var(--primaryTextCol);
  }

  &__os-icon {
    display: inline-block;
    width: 16px;
    height: 16px;
    background-position: center;
    background-repeat: no-repeat;
    background-size: contain;

    &--mac {
      background-image: url("@/assets/apple-desktop-icon.svg");
    }

    &--windows {
      background-image: url("@/assets/windows-desktop-icon.svg");
    }
  }
}
</style>
